'use client';

import { Container } from '@/components/primitives/Container/Container';
import { toPascalCase } from '@/utils/string-utils';
import { IBaseComponentProps, WithMetadata } from '@/lib/types';
import { IProgressBarModularBlock } from '@/.generated';
import PlainTextWrapper from '@/helpers/Wrappers/PlainTextWrapper/PlainTextWrapper';
import { defaultVariants } from './ProgressBar.styles';
import { useGlobalLabels } from '@/context/GlobalLabelContext';

type ProgressBarProps = WithMetadata<IProgressBarModularBlock> & IBaseComponentProps;

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

// Formats an ISO date string ("YYYY-MM-DD") into "MMM D, YYYY" without timezone drift
const formatLastUpdated = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateFormatter.format(date);
};

// Renders the default ProgressBar skin
const Default = (props: ProgressBarProps) => {
  const { componentName, _metadata, inline_navigation_id, show_last_updated_date, hide_component } =
    props;
  const {
    wrapper,
    header,
    headerText,
    title,
    lastUpdated,
    percentageWrapper,
    percentage,
    track,
    fill,
  } = defaultVariants();
  const { globalLabels } = useGlobalLabels();

  if (hide_component) return null;

  const percent = props.progress_percentage;
  const formattedDate = formatLastUpdated(props.last_updated);
  const fillColor = props.progress_bar_color?.color_list || 'var(--theme-color-accent-orange)';

  return (
    <Container componentName={componentName} metaId={_metadata?.uid} id={inline_navigation_id}>
      <div className={wrapper()}>
        <div className={header()}>
          <div className={headerText()}>
            <PlainTextWrapper
              content={props.title}
              cslpAttribute={props.$?.title}
              className={title()}
            />
            {show_last_updated_date && formattedDate && (
              <PlainTextWrapper
                content={`${globalLabels.last_updated_label} ${formattedDate}`}
                cslpAttribute={props.$?.last_updated}
                className={lastUpdated()}
              />
            )}
          </div>
          {!!percent && (
            <div className={percentageWrapper()}>
              <PlainTextWrapper
                content={`${percent}%`}
                cslpAttribute={props.$?.progress_percentage}
                className={percentage()}
              />
            </div>
          )}
        </div>
        {!!percent && (
          <div
            className={track()}
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuetext={`${percent}%`}
            aria-label={props.title || 'Progress'}
          >
            <div className={fill()} style={{ width: `${percent}%`, backgroundColor: fillColor }} />
          </div>
        )}
      </div>
    </Container>
  );
};

const variants = {
  Default,
};

// Resolves the requested variant and renders the ProgressBar component
export const ProgressBar = (props: ProgressBarProps) => {
  const Component =
    (props.component_variant &&
      variants[toPascalCase(props.component_variant) as keyof typeof variants]) ||
    Default;
  return <Component {...props} />;
};
